import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Sharp Material Design Icons as SVG components
const MailIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M22 4H2v16h20V4zM4 6l8 5 8-5v2l-8 5-8-5V6z"/>
  </svg>
);

const MessageIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 2H4L2 4v18l4-4h14V2z"/>
  </svg>
);

const PresentationIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 22h12l-6-6zM23 3H1v16h4v-2H3V5h18v12h-2v2h4V3z"/>
  </svg>
);

const FileTextIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M14 2H4v20h16V8l-6-6zm4 18H6V4h7v5h5v11z"/>
  </svg>
);

const SheetIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M10 10h5v11h-5zm7 11h5V10h-5zM22 3H2v5h20zM2 10v11h5V10z"/>
  </svg>
);

const AssessmentIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M21 3H3v18h18V3zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
  </svg>
);

const VideoIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17 10.5V6H3v12h14v-4.5l4 4v-11l-4 4z"/>
  </svg>
);

const UserIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M21 3H3v18h18V3zm-9 6c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm6 10H6v-1c0-2 4-3.1 6-3.1s6 1.1 6 3.1v1z"/>
  </svg>
);

interface SpiralNode {
  id: string;
  startX: number;
  startY: number;
  iconComponent: React.ComponentType<any>;
  startTime: number;
}

export const SpiralLearningAnimation: React.FC = () => {
  const [nodes, setNodes] = useState<SpiralNode[]>([]);
  const nodeCounter = useRef(0);

  // Memoize constants to prevent recalculations
  const constants = useMemo(() => ({
    ICONS: [MailIcon, MessageIcon, PresentationIcon, FileTextIcon, SheetIcon, AssessmentIcon, VideoIcon, UserIcon],
    CENTER: { x: 300, y: 200 },
    ANIMATION_DURATION: 3, // seconds
    SPAWN_INTERVAL: 600, // ms
    DISTANCE: 250
  }), []);



  // Optimized interval with batch operations
  useEffect(() => {
    let mounted = true;
    
    const interval = setInterval(() => {
      if (!mounted) return;
      
      setNodes(prev => {
        const now = Date.now();
        // Filter old nodes and add new one in single operation
        const filteredNodes = prev.filter(node => 
          now - node.startTime < constants.ANIMATION_DURATION * 1000
        );
        
        // Create new node
        const randomIcon = constants.ICONS[Math.floor(Math.random() * constants.ICONS.length)];
        const angle = Math.random() * 2 * Math.PI;
        const startX = constants.CENTER.x + Math.cos(angle) * constants.DISTANCE;
        const startY = constants.CENTER.y + Math.sin(angle) * constants.DISTANCE;
        
        const newNode: SpiralNode = {
          id: `node-${nodeCounter.current++}`,
          startX,
          startY,
          iconComponent: randomIcon,
          startTime: now
        };
        
        return [...filteredNodes, newNode];
      });
    }, constants.SPAWN_INTERVAL);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [constants]);

  // Memoize animation transition
  const animationTransition = useMemo(() => ({
    duration: constants.ANIMATION_DURATION,
    times: [0, 0.1, 0.5, 0.7, 0.701],
    ease: "easeOut"
  }), [constants.ANIMATION_DURATION]);

  return (
    <div className="w-full h-full min-h-[400px] flex items-center justify-center">
      <svg
        viewBox="-20 -20 640 440"
        className="w-full h-full max-w-[600px] max-h-[400px]"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <radialGradient id="spiralNodeGradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#4040FF" />
            <stop offset="100%" stopColor="#0000bd" />
          </radialGradient>
          <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#0000bd" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#4040FF" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Center glow */}
        <motion.circle
          cx={constants.CENTER.x}
          cy={constants.CENTER.y}
          r="60"
          fill="url(#centerGlow)"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        {/* Moving nodes */}
        <AnimatePresence mode="popLayout">
          {nodes.map((node) => {
            const IconComponent = node.iconComponent;
            
            return (
              <motion.g key={node.id}>
                {/* Node circle */}
                <motion.circle
                  r="24"
                  fill="url(#spiralNodeGradient)"
                  initial={{ 
                    cx: node.startX,
                    cy: node.startY,
                    scale: 0,
                    opacity: 0
                  }}
                  animate={{
                    cx: constants.CENTER.x,
                    cy: constants.CENTER.y,
                    scale: [0, 1, 1, 1, 0],
                    opacity: [0, 1, 1, 0.1, 0]
                  }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={animationTransition}
                />
                
                {/* Node icon */}
                <motion.foreignObject
                  width="32"
                  height="32"
                  initial={{ 
                    x: node.startX - 16,
                    y: node.startY - 16,
                    opacity: 0
                  }}
                  animate={{
                    x: constants.CENTER.x - 16,
                    y: constants.CENTER.y - 16,
                    opacity: [0, 1, 1, 0.1, 0]
                  }}
                  exit={{ opacity: 0 }}
                  transition={animationTransition}
                >
                  <div className="flex items-center justify-center w-full h-full text-white">
                    <IconComponent />
                  </div>
                </motion.foreignObject>
              </motion.g>
            );
          })}
        </AnimatePresence>

        {/* Center LogOmark */}
        <motion.g
          animate={{ scale: [1, 1.02, 1] }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <image
            x={constants.CENTER.x - 36}
            y={constants.CENTER.y - 36}
            width="72"
            height="72"
            href="/LOGOmark.png"
            preserveAspectRatio="xMidYMid meet"
          />
        </motion.g>

        {/* Pulse rings */}
        {[0, 1, 2].map((index) => (
          <motion.circle
            key={`pulse-${index}`}
            cx={constants.CENTER.x}
            cy={constants.CENTER.y}
            r="30"
            fill="none"
            stroke="#4040FF"
            strokeWidth="1"
            strokeOpacity="0.3"
            initial={{ scale: 1, opacity: 0.3 }}
            animate={{ scale: 2.5, opacity: 0 }}
            transition={{
              duration: 4,
              repeat: Infinity,
              delay: index * 1.3,
              ease: "easeOut"
            }}
          />
        ))}
      </svg>
    </div>
  );
}; 